import React, { useState, useEffect } from 'react';
import { Briefcase, X, Check, Loader } from 'lucide-react';
import './JobPickerModal.scss';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface Job {
  id: number;
  title: string;
  location?: string;
  status: string;
  published_at?: string;
}

interface JobPickerModalProps {
  isOpen: boolean;
  cvIds: number[];
  onClose: () => void;
  onSuccess: (added: number, skipped: number) => void;
}

const JobPickerModal: React.FC<JobPickerModalProps> = ({ isOpen, cvIds, onClose, onSuccess }) => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setSelectedJobId(null);
    setError('');
    fetchJobs();
  }, [isOpen]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/jobs/my/jobs`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        const jobList = result.data?.jobs || result.data || result || [];
        const published = Array.isArray(jobList)
          ? jobList.filter((j: Job) => j.status === 'published')
          : [];
        setJobs(published);
      }
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedJobId) return;
    try {
      setSubmitting(true);
      setError('');
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/job-applications/bulk-add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ job_id: selectedJobId, cv_ids: cvIds }),
      });

      if (response.ok) {
        const result = await response.json();
        onSuccess(result.added, result.skipped);
      } else {
        const err = await response.json();
        setError(err.message || 'Nepavyko pridėti kandidatų');
      }
    } catch {
      setError('Tinklo klaida. Bandykite dar kartą.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="job-picker-overlay" onClick={onClose}>
      <div className="job-picker-modal" onClick={(e) => e.stopPropagation()}>
        <button className="job-picker-close" onClick={onClose}>
          <X size={20} />
        </button>

        <div className="job-picker-header">
          <Briefcase size={24} />
          <h3>Įkelti į darbo skelbimą</h3>
          <p>Pasirinkta kandidatų: <strong>{cvIds.length}</strong></p>
        </div>

        <div className="job-picker-body">
          {loading ? (
            <div className="job-picker-loading">
              <Loader size={24} className="spinner" />
              <span>Kraunami skelbimai...</span>
            </div>
          ) : jobs.length === 0 ? (
            <div className="job-picker-empty">
              <p>Neturite aktyvių darbo skelbimų.</p>
              <p>Sukurkite skelbimą, kad galėtumėte pridėti kandidatus.</p>
            </div>
          ) : (
            <ul className="job-picker-list">
              {jobs.map((job) => (
                <li
                  key={job.id}
                  className={`job-picker-item ${selectedJobId === job.id ? 'selected' : ''}`}
                  onClick={() => setSelectedJobId(job.id)}
                >
                  <div className="job-picker-item-info">
                    <span className="job-title">{job.title}</span>
                    {job.location && <span className="job-location">{job.location}</span>}
                  </div>
                  {selectedJobId === job.id && <Check size={18} />}
                </li>
              ))}
            </ul>
          )}

          {error && <p className="job-picker-error">{error}</p>}
        </div>

        <div className="job-picker-actions">
          <button
            className="btn-primary"
            onClick={handleSubmit}
            disabled={!selectedJobId || submitting}
          >
            {submitting ? 'Pridedama...' : 'Pridėti'}
          </button>
          <button className="btn-secondary" onClick={onClose}>
            Atšaukti
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobPickerModal;
